import { styled } from '@stitches/configed';
import { useMergedState } from 'rc-util';
import { LoadingOutlined } from '@ant-design/icons';
import Upload, { RcFile } from 'antd/lib/upload';
import React, { ReactNode, useEffect } from 'react';
import {
  Photo,
  uploadAvatar,
  uploadFile,
  uploadImage,
} from '../../../services/attachment';
import { CropOrNotWrapper } from './CronOrNotWrapper';
import { handleAxiosError } from '../../../utils';
import { DeleteBin6LineIcon, FileUploadLineIcon } from '../Icons';
import { Image } from '../Image';

type Props = {
  type?: 'avatar';
  id?: string;
  role: 'customer' | 'shipper' | 'merchant' | 'admin';
  crop?: boolean;
  onUploadSuccess?: (imageUrl: string | Photo) => void;
  onUploadding?: () => void;
  onUploadFinished?: () => void;
  width?: number;
  height?: number;
  allowFiles?: string[];
  disable?: boolean;
  onRemove?: () => void;

  label: ReactNode;
  description: ReactNode;
  initialValues: {
    title: string;
    url?: string;
    type?: string;
    filename?: string;
    isUploading?: boolean;
  }[];

  accept?: string;
} & (
  | {
      mode: 'single';
      value?: string;
      onChange?: (value: string | undefined) => void;
    }
  | {
      mode: 'multiple';
      value?: string[];
      onChange?: (value: (string | undefined)[] | undefined) => void;
    }
);

export const Upload2 = React.forwardRef(
  (
    {
      type,
      role,
      crop,
      width,
      height,
      onUploadSuccess,
      onUploadding,
      onUploadFinished,
      allowFiles = ['image/jpeg', 'image/png'],
      disable = false,
      onRemove,

      label,
      description,
      initialValues,

      accept,
      ...props
    }: Props,
    ref: any,
  ) => {
    const [items, setItems] = useMergedState(initialValues, {
      onChange: (value) => {
        if (props.mode === 'single') {
          props.onChange?.(value[0].url);
        } else if (props.mode === 'multiple') {
          props.onChange?.(value.map((x) => x.url));
        }
      },
    });

    useEffect(() => {
      if (props.mode === 'single') {
        setItems((pre) => {
          pre[0].url = props.value;
          return [...pre];
        });
      }
    }, [props.value, setItems, props.mode]);

    const beforeUpload = async (index: number, file: RcFile) => {
      onUploadding?.(); // parent
      items[index].isUploading = true;
      setItems([...items]);
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (type === 'avatar') {
          const resUpload = await uploadAvatar(role, formData);
          // await new Promise((resolve) => setTimeout(resolve, 3000));
          // const resUpload = {
          //   data: {
          //     data: {
          //       url: 'https://static.yougo.vn/uploads/2024-06/yougo1_avatar_7ddbba4f-99c3-4724-9495-ebcdf18cf6e4.png',
          //     },
          //   },
          // };
          onUploadSuccess?.(resUpload.data.data.url);
          items[index].url = resUpload.data.data.url;
          setItems([...items]);
        } else if (file.type.includes('image')) {
          const resUpload = await uploadImage(role, formData);
          onUploadSuccess?.(resUpload.data.data.url);
          items[index].url = resUpload.data.data.url;
          setItems([...items]);
        } else {
          const resUpload = await uploadFile(role, formData);
          onUploadSuccess?.(resUpload.data.data.url);
          items[index].url = resUpload.data.data.url;
          setItems([...items]);
        }
      } catch (error) {
        items[index].url = undefined;
        setItems([...items]);
        handleAxiosError(error);
      } finally {
        items[index].isUploading = false;
        setItems([...items]);

        onUploadFinished?.(); // parent
      }

      return false;
    };

    return (
      <Upload2Wrapper ref={ref}>
        <div className="upload-left">
          <label className="upload-label">{label}</label>
          <div className="upload-description">{description}</div>
        </div>
        <div className="upload-right">
          {items.map((x, index) => {
            return (
              <div key={index}>
                <div className={'upload-item-header'}>{x.title}</div>
                <div className="uploader-wrapper">
                  <CropOrNotWrapper enable={crop} width={width} height={height}>
                    <Upload
                      listType="picture-card"
                      className="avatar-uploader"
                      showUploadList={false}
                      accept={accept}
                      beforeUpload={(file) => {
                        return beforeUpload(index, file);
                      }}
                    >
                      {x.url ? (
                        <>
                          <div
                            className="file-preview-wrapper"
                            style={{ height: height ? height + 32 : undefined }}
                          >
                            {['jpg', 'png'].includes(
                              x.url.split('.').at(-1) || '',
                            ) ? (
                              <Image
                                className="file-preview-image"
                                src={x.url}
                                alt="avatar"
                                style={{
                                  width: width,
                                  height: height,
                                }}
                              />
                            ) : (
                              <div className="file-preview-any">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{ minWidth: 16 }}
                                >
                                  <path
                                    d="M9.88487 5.17093L6.11421 8.94227C6.05053 9.00376 5.99975 9.07733 5.96481 9.15866C5.92987 9.24 5.91148 9.32748 5.91071 9.416C5.90994 9.50452 5.92681 9.59231 5.96033 9.67424C5.99385 9.75617 6.04335 9.8306 6.10594 9.8932C6.16854 9.95579 6.24297 10.0053 6.3249 10.0388C6.40684 10.0723 6.49462 10.0892 6.58314 10.0884C6.67166 10.0877 6.75914 10.0693 6.84048 10.0343C6.92181 9.99939 6.99538 9.94861 7.05687 9.88493L10.8282 6.11427C11.2033 5.73916 11.414 5.23041 11.414 4.69993C11.414 4.16945 11.2033 3.6607 10.8282 3.2856C10.4531 2.91049 9.94435 2.69976 9.41387 2.69976C8.8834 2.69976 8.37465 2.91049 7.99954 3.2856L4.22821 7.05693C3.91226 7.36502 3.66063 7.73277 3.48791 8.13886C3.3152 8.54496 3.22484 8.9813 3.22207 9.42259C3.21931 9.86387 3.30419 10.3013 3.47181 10.7095C3.63942 11.1178 3.88642 11.4886 4.19848 11.8007C4.51055 12.1127 4.88146 12.3596 5.28971 12.5272C5.69796 12.6947 6.13541 12.7796 6.5767 12.7767C7.01798 12.7739 7.45431 12.6835 7.86038 12.5107C8.26645 12.3379 8.63417 12.0863 8.94221 11.7703L12.7135 7.9996L13.6562 8.94227L9.88487 12.7136C9.45151 13.147 8.93704 13.4907 8.37082 13.7253C7.80461 13.9598 7.19774 14.0805 6.58487 14.0805C5.97201 14.0805 5.36514 13.9598 4.79893 13.7253C4.23271 13.4907 3.71824 13.147 3.28487 12.7136C2.85151 12.2802 2.50775 11.7658 2.27322 11.1995C2.03868 10.6333 1.91797 10.0265 1.91797 9.4136C1.91797 8.80073 2.03868 8.19387 2.27322 7.62765C2.50775 7.06144 2.85151 6.54696 3.28487 6.1136L7.05687 2.34293C7.68555 1.73574 8.52756 1.39976 9.40154 1.40735C10.2755 1.41495 11.1116 1.76551 11.7296 2.38354C12.3476 3.00156 12.6982 3.83761 12.7058 4.7116C12.7134 5.58559 12.3774 6.42759 11.7702 7.05627L7.99954 10.8289C7.81376 11.0147 7.59322 11.162 7.35051 11.2625C7.1078 11.363 6.84767 11.4147 6.58497 11.4147C6.32227 11.4146 6.06216 11.3629 5.81947 11.2623C5.57678 11.1618 5.35627 11.0144 5.17054 10.8286C4.98481 10.6428 4.83748 10.4223 4.73698 10.1796C4.63648 9.93686 4.58477 9.67673 4.5848 9.41403C4.58483 9.15133 4.63661 8.89122 4.73716 8.64853C4.83772 8.40584 4.9851 8.18533 5.17087 7.9996L8.94221 4.22827L9.88487 5.17093Z"
                                    fill="#374FF1"
                                  />
                                </svg>
                                <span>
                                  {x.filename
                                    ? x.filename
                                    : x.url.split('/').at(-1)}
                                </span>
                              </div>
                            )}

                            <div className="upload-actions">
                              <FileUploadLineIcon style={{ fontSize: 24 }} />

                              <DeleteBin6LineIcon
                                style={{ fontSize: 24 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setItems((pre) => {
                                    pre[index].url = undefined;
                                    return [...items];
                                  });
                                  if (onRemove) onRemove();
                                }}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div
                          className="upload-item-plus"
                          style={{ width: width, height: height }}
                        >
                          {x.isUploading ? (
                            <LoadingOutlined
                              style={{ fontSize: 30, strokeWidth: 2 }}
                            />
                          ) : (
                            <FileUploadLineIcon style={{ fontSize: 40 }} />
                          )}
                        </div>
                      )}
                    </Upload>
                  </CropOrNotWrapper>
                  {x.url && <div className="upload-mask" />}
                </div>
              </div>
            );
          })}
        </div>
      </Upload2Wrapper>
    );
  },
);

const Upload2Wrapper = styled('div', {
  display: 'flex',
  '& .upload-left': {
    maxWidth: 240,
    marginRight: 10,
    '& .upload-description': {
      marginTop: 10,
    },
  },
  '& .upload-label': {
    fontWeight: 600,
    color: '$content-secondary',
  },
  '& .upload-right': {
    display: 'flex',
    gap: 16,
    '& .upload-item-header': {
      background: '$bg-teriary',
      textAlign: 'center',
      padding: 4,
      marginBottom: 2,
      fontWeight: 500,
      color: '$content-primary',
      lineHeight: '24px',
      borderRadius: 6,
    },
  },

  '& .uploader-wrapper': {
    position: 'relative',
    '& .upload-mask': {
      zIndex: 1,
      position: 'absolute',
      inset: 0,
    },
  },
  '& .ant-upload.ant-upload-disabled': {
    cursor: 'default',
  },
  '& .ant-upload.ant-upload-select-picture-card': {
    background: 'White',
    marginRight: 0,
    marginBottom: 0,
    borderWidth: 2,
    width: 'auto',
    height: 'auto',
    // width: 330,
    // height: 282,
  },
  '& .file-preview-wrapper': {
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    minHeight: 150,
    minWidth: 260,
    '& .file-preview-image': {
      borderRadius: 12,
    },
    '& .file-preview-any': {
      flex: 1,
      textAlign: 'left',
      maxWidth: 180,
      height: 'fit-content',
      fontWeight: 500,
      padding: '6px 8px',
      borderRadius: 12,
      background: '$bg-light-accent',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      wordBreak: 'break-all',
    },
    '& .upload-actions': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignSelf: 'stretch',
      '& svg': {
        cursor: 'pointer',
        zIndex: 100,
        '&:hover': {
          opacity: 0.8,
        },
      },
    },
  },
  '& .upload-item-plus': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 260,
    minHeight: 150,
  },
});
